import { useCallback, useEffect, useRef, useState } from 'react';

function pickMimeType() {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

/**
 * Gravação estilo WhatsApp: iniciar → cronômetro; cancelar descarta; confirmar gera File e chama onFile.
 */
export function useInternalChatVoiceRecorder({ onFile, onError }) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const discardRef = useRef(false);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!isRecording) {
      setRecordingSeconds(0);
      return undefined;
    }
    const id = window.setInterval(() => {
      setRecordingSeconds((s) => s + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [isRecording]);

  const stopRecorder = useCallback(() => {
    const mr = recorderRef.current;
    if (!mr || mr.state === 'inactive') {
      discardRef.current = false;
      recorderRef.current = null;
      stopStream();
      setIsRecording(false);
      return;
    }
    mr.stop();
  }, [stopStream]);

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      onError?.(new Error('unsupported'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      discardRef.current = false;
      const mimeType = pickMimeType();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        const discard = discardRef.current;
        discardRef.current = false;
        const type = mr.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        recorderRef.current = null;
        stopStream();
        setIsRecording(false);
        if (discard || blob.size === 0) return;
        const ext = type.includes('webm') ? 'webm' : type.includes('mp4') ? 'm4a' : 'webm';
        const file = new File([blob], `audio-${Date.now()}.${ext}`, { type });
        onFile?.(file);
      };

      mr.onerror = () => {
        discardRef.current = false;
        recorderRef.current = null;
        stopStream();
        setIsRecording(false);
        onError?.(new Error('recorder'));
      };

      mr.start(250);
      setIsRecording(true);
    } catch (err) {
      discardRef.current = false;
      stopStream();
      setIsRecording(false);
      onError?.(err);
    }
  }, [onError, onFile, stopStream]);

  const cancelRecording = useCallback(() => {
    discardRef.current = true;
    stopRecorder();
  }, [stopRecorder]);

  const confirmRecording = useCallback(() => {
    discardRef.current = false;
    stopRecorder();
  }, [stopRecorder]);

  useEffect(
    () => () => {
      discardRef.current = true;
      const mr = recorderRef.current;
      if (mr && mr.state === 'recording') {
        try {
          mr.stop();
        } catch {
          /* noop */
        }
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    },
    []
  );

  return {
    isRecording,
    recordingSeconds,
    startRecording,
    confirmRecording,
    cancelRecording,
  };
}
